import React, { useState, useEffect } from 'react'
import UploaderComponent from './UploaderComponent'
import uploadMutation from './gql/uploadImgBase64'
import deleteMutation from './gql/deleteImg'
import { updateQuery, useMutation } from 'components/graphql'
import PropTypes from 'prop-types'
import set from 'lodash/set'
import get from 'lodash/get'

import { Storage, Cache } from 'aws-amplify'
import { Auth } from 'aws-amplify'
import { useField, useFormApi } from 'informed'
import useSafeState from 'hooks/useSafeState'
/*
	upload entity(product, category, etc.) specifuc image via grapql mutation,
	id prop is an entity id, it uploads both to s3 and to the DB
*/
export const ImageUploaderGraphql = ({ onUpload, onRemove, onCompleted, updateCacheFunction, ...props }) => {
	const [isLoading, setIsLoading] = useState(false)
	useEffect(() => {
		if(isLoading) {
			setIsLoading(false)
		}
	}, [props.imgKey])	// eslint-disable-line react-hooks/exhaustive-deps

	const updateCache = updateQuery({
		query: props.getQuery,
		variables: { id: props.id }
	}, (draft, { mutationResult, readQuery, queryField }) => {
		if(typeof updateCacheFunction === 'function') {
			updateCacheFunction(draft, { mutationResult, readQuery, queryField })
		} else {
			const { field = 'img' } = props
			const newImg = typeof mutationResult === 'object' ? mutationResult : null
			set(draft, `${queryField}.${field}`, newImg)
		}
	})

	const handleUpload = upload => base64 => {
		const { type, id } = props
		setIsLoading(true)
		upload({
			variables: {
				id,
				type,
				file: base64
			},
			update: updateCache
		})
		if(onUpload) {
			onUpload({ type, id }, base64)
		}
	}

	const removeImage = delFunction => e => {
		const { type, id } = props
		delFunction({
			variables: { type, id },
			optimisticResponse: {
				__typename: 'Mutation',
				cashAssist: {
					deleteImg: id,
					__typename: 'CA_Mutation'
				}
			},
			update: updateCache
		})
		if(onRemove) {
			onRemove({ type, id })
		}
	}

	const handleComplete = (mutationData) => {
		if(typeof onCompleted === 'function') {
			// Get 'key' from mutation result depends on mutation
			const key = mutationData.deleteImg ? null : get(mutationData, 'img.key')
			onCompleted(key)
		}
	}

	const [delImg] = useMutation(deleteMutation, { onCompleted: handleComplete, withIndicator: false })
	const [uploadImg] = useMutation(uploadMutation, { onCompleted: handleComplete, withIndicator: false })

	return (
		<UploaderComponent
			{...props}
			isUploading={isLoading}
			handleDelete={removeImage(delImg)}
			handleUpload={handleUpload(uploadImg)}
			fileToString
		/>
	)
}

ImageUploaderGraphql.propTypes = {
	type: PropTypes.string.isRequired,
	id: PropTypes.string.isRequired,
	onUpload: PropTypes.func,
	onRemove: PropTypes.func
}


/*
	this is used to upload images directly to S3 with static imgKey,
	but if we have type and id props provided withGraphqlUpload will be used
*/
export const ImageUploaderS3 = ({ s3UploadKey, onUpload, onRemove, noKeyPrefix, ...props }) => {
	const [isLoading, setIsLoading] = useSafeState(false)
	const { imgKey } = props

	const handleUpload = async file => {
		setIsLoading(true)
		const keyBase = (await Auth.currentSession()).getIdToken().payload['custom:company'].replace(/-/g, '_')
		const timeStampHash = (+new Date()).toString(36)
		const newImgKey = `${keyBase}/${s3UploadKey}_${timeStampHash}`
		const uploadPromise = Storage.put(newImgKey, file, {
			...noKeyPrefix && { customPrefix: { public: '' } },
			cacheControl: 'private, max-age=172800',
			contentType: file.type
		})
		Cache.removeItem(imgKey)
		Storage.remove(imgKey)
		if(onUpload) {
			onUpload(newImgKey, file)
		}
		await uploadPromise
		setIsLoading(false)
	}

	const handleDelete = async () => {
		Cache.removeItem(imgKey)
		if(onRemove) {
			onRemove(imgKey)
		}
		return await Storage.remove(imgKey)
	}

	return(
		<UploaderComponent handleDelete={handleDelete} handleUpload={handleUpload} isUploading={isLoading} {...props}/>
	)
}
ImageUploaderS3.propTypes = {
	imgKey: PropTypes.string,
	onUpload: PropTypes.func,
	onRemove: PropTypes.func
}

export const ImageUploaderInForm = ({ field, onUpload, onRemove, ...props }) => {
	const { fieldState, fieldApi } = useField({ field })
	const formApi = useFormApi()

	return(
		<ImageUploaderS3
			src={fieldState.value}
			handleUpload={file => {
				fieldApi.setValue(file)
				formApi.submitForm()
			}}
			handleDelete={(key, src) => {
				fieldApi.setValue(null)
				formApi.submitForm()
			}}
			fileToString={true}
			{...props}
		/>
	)
}

export default UploaderComponent
